import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SURVEY_QUESTIONS } from "@/components/founding/surveyData";
import { submitFoundingMember } from "@/lib/api";

const TOTAL_STEPS = SURVEY_QUESTIONS.length; // 4 survey steps; step 5 = signup; step 6 = success

export default function FoundingMemberModal({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset after close animation
      const t = setTimeout(() => {
        setStep(0);
        setAnswers({});
        setForm({ name: "", email: "", phone: "" });
        setSubmitting(false);
        setSubmitted(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const onSurvey = step < TOTAL_STEPS;
  const onSignup = step === TOTAL_STEPS && !submitted;
  const onDone = submitted;

  const currentQ = onSurvey ? SURVEY_QUESTIONS[step] : null;
  const currentSelections = currentQ ? (answers[currentQ.id] || []) : [];

  const toggleOption = (opt) => {
    setAnswers((prev) => {
      const cur = prev[currentQ.id] || [];
      const next = cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt];
      return { ...prev, [currentQ.id]: next };
    });
  };

  const canAdvance = onSurvey ? currentSelections.length > 0 : true;
  const isLastSurveyStep = step === TOTAL_STEPS - 1;

  const goNext = () => {
    if (onSurvey) setStep(step + 1);
  };
  const goBack = () => {
    if (step > 0 && !submitted) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        survey: {
          q1_where_with_matcha: answers.q1_where_with_matcha || [],
          q2_when_visiting: answers.q2_when_visiting || [],
          q3_first_order: answers.q3_first_order || [],
          q4_matcha_at_home: answers.q4_matcha_at_home || [],
        },
      };
      await submitFoundingMember(payload);
      setSubmitted(true);
      toast.success("Welcome to the Founding 100.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something didn't pour right. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = onDone
    ? 100
    : Math.round(((step + (onSignup ? 1 : 0)) / (TOTAL_STEPS + 1)) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#F7F5F0] border border-border rounded-3xl p-0 overflow-hidden gap-0"
        data-testid="founding-modal"
      >
        <div className="px-8 md:px-12 pt-8 md:pt-10">
          <div className="flex items-center justify-between">
            <p className="overline text-[#C47253]" data-testid="modal-step-indicator">
              {onDone
                ? "Welcome, founder"
                : onSignup
                ? "Last step · Your details"
                : `Question ${step + 1} of ${TOTAL_STEPS}`}
            </p>
            <span className="font-serif italic text-[#2C4C3B] text-sm">Hontō · 本当</span>
          </div>
          <div className="mt-4 h-[2px] w-full bg-border rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#2C4C3B]"
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="modal-progress-bar"
            />
          </div>
        </div>

        <div className="px-8 md:px-12 pb-10 pt-8 min-h-[420px]">
          <AnimatePresence mode="wait">
            {onSurvey && (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <h2
                  className="font-serif text-3xl md:text-4xl leading-tight text-[#1A2B22]"
                  data-testid={`modal-question-title-${step}`}
                >
                  {currentQ.title}
                </h2>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C7365] mt-3">
                  Select all that apply
                </p>

                <div className="mt-7 space-y-3" data-testid={`modal-options-${step}`}>
                  {currentQ.options.map((opt, i) => {
                    const selected = currentSelections.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => toggleOption(opt)}
                        data-testid={`modal-option-q${step + 1}-${i}`}
                        className={[
                          "w-full text-left rounded-2xl border px-5 py-4 flex items-center gap-4 transition-all duration-300",
                          selected
                            ? "bg-[#EBEFDE] border-[#2C4C3B]"
                            : "bg-white border-border hover:border-[#2C4C3B]/40 hover:-translate-y-[1px]",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "w-5 h-5 rounded-md flex items-center justify-center shrink-0 border transition-colors",
                            selected
                              ? "bg-[#2C4C3B] border-[#2C4C3B]"
                              : "bg-white border-[#DDE3D5]",
                          ].join(" ")}
                        >
                          {selected && <Check className="w-3.5 h-3.5 text-[#F7F5F0]" strokeWidth={3} />}
                        </span>
                        <span className="text-[15px] text-[#1A2B22]">{opt}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {onSignup && (
              <motion.form
                key="signup"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                onSubmit={handleSubmit}
                data-testid="modal-signup-form"
              >
                <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#1A2B22]">
                  Hold your seat at the counter.
                </h2>
                <p className="text-[#5C7365] mt-3 text-sm">
                  We&apos;ll email your founding pass before we open. No newsletters, only the moments that matter.
                </p>

                <div className="mt-7 space-y-5">
                  <div>
                    <Label htmlFor="fm-name" className="text-xs uppercase tracking-[0.18em] text-[#5C7365]">Name</Label>
                    <Input
                      id="fm-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="signup-name-input"
                      className="mt-2 h-12 rounded-xl bg-white border-border focus-visible:ring-[#2C4C3B]"
                      placeholder="What should we call you?"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fm-email" className="text-xs uppercase tracking-[0.18em] text-[#5C7365]">Email</Label>
                    <Input
                      id="fm-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid="signup-email-input"
                      className="mt-2 h-12 rounded-xl bg-white border-border focus-visible:ring-[#2C4C3B]"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fm-phone" className="text-xs uppercase tracking-[0.18em] text-[#5C7365]">Phone (optional)</Label>
                    <Input
                      id="fm-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="signup-phone-input"
                      className="mt-2 h-12 rounded-xl bg-white border-border focus-visible:ring-[#2C4C3B]"
                      placeholder="+91 · for opening day SMS only"
                    />
                  </div>
                </div>
              </motion.form>
            )}

            {onDone && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center py-6"
                data-testid="modal-success"
              >
                <div className="w-16 h-16 rounded-full bg-[#2C4C3B] flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7 text-[#F7F5F0]" strokeWidth={2.5} />
                </div>
                <h2 className="mt-7 font-serif text-3xl md:text-4xl text-[#1A2B22]">
                  Your bowl is waiting.
                </h2>
                <p className="mt-4 text-[#5C7365] max-w-md mx-auto">
                  You&apos;re officially among the founding hundred at Hontō Matcha. We&apos;ll be in touch
                  before we open the doors in Dehradun.
                </p>
                <Button
                  onClick={() => onOpenChange(false)}
                  data-testid="modal-close-success"
                  className="mt-9 rounded-full bg-primary hover:bg-[#1F382B] text-primary-foreground h-12 px-8 text-xs tracking-[0.2em] uppercase"
                >
                  Return to slow
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!onDone && (
          <div className="border-t border-border px-8 md:px-12 py-5 flex items-center justify-between bg-[#F1EEE6]/50">
            <Button
              type="button"
              variant="ghost"
              onClick={goBack}
              disabled={step === 0}
              data-testid="modal-back-btn"
              className="rounded-full text-[#2C4C3B] hover:bg-[#EBEFDE] disabled:opacity-30 h-11 px-5 text-xs tracking-[0.18em] uppercase"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            {onSurvey ? (
              <Button
                type="button"
                onClick={isLastSurveyStep ? () => setStep(TOTAL_STEPS) : goNext}
                disabled={!canAdvance}
                data-testid="modal-next-btn"
                className="rounded-full bg-primary hover:bg-[#1F382B] text-primary-foreground disabled:opacity-40 h-11 px-6 text-xs tracking-[0.2em] uppercase"
              >
                {isLastSurveyStep ? "Continue to signup" : "Next"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="signup-submit-btn"
                className="rounded-full bg-primary hover:bg-[#1F382B] text-primary-foreground disabled:opacity-50 h-11 px-6 text-xs tracking-[0.2em] uppercase"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reserving</>
                ) : (
                  <>Claim my spot <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
